import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { FieldPosition, LivePlayer } from '../liveMatchTypes.ts';

// HOME (sinistra): col 0 = fondo, col 1 = rete
const HOME_COLS: number[][] = [[5, 6, 1], [4, 3, 2]];
// AWAY (destra): col 0 = rete, col 1 = fondo
const AWAY_COLS: number[][] = [[2, 3, 4], [1, 6, 5]];

const ROLE_SHORT: Record<string, string> = {
  PALLEGGIO: 'PAL', OPPOSTO: 'OPP', BANDA: 'BAN', CENTRALE: 'CEN', LIBERO: 'LIB',
};

// ─── PlayerCircle ─────────────────────────────────────────────────────────────

interface CircleProps {
  pos: number;
  fieldPos: FieldPosition | undefined;
  player: LivePlayer | undefined;
  shirtColor: string;
  setterColor: string;
}

function PlayerCircle({ pos, fieldPos, player, shirtColor, setterColor }: CircleProps) {
  const isLibero        = fieldPos?.isLibero ?? false;
  const isEmpty         = !fieldPos || !player;
  const isPalleggiatore = fieldPos?.role === 'PALLEGGIO';

  const circleBg    = isLibero ? '#f9a825' : isEmpty ? 'rgba(255,255,255,0.08)' : isPalleggiatore ? '#000' : shirtColor;
  const circleColor = isLibero ? '#000' : isPalleggiatore ? '#FFD700' : '#fff';
  const borderColor = isLibero ? '#e65100' : isEmpty ? 'rgba(255,255,255,0.2)' : isPalleggiatore ? '#FFD700' : `${shirtColor}bb`;
  const roleLabel   = fieldPos?.role ? (ROLE_SHORT[fieldPos.role] ?? fieldPos.role[0]) : null;

  const shirtDisplay = player?.shirtNumber || '?';
  const tooltipTitle = player
    ? `${pos} · #${player.shirtNumber}${player.lastName ? ` ${player.lastName}` : ''}${fieldPos?.role ? ` · ${fieldPos.role}` : ''}`
    : `Pos ${pos} — vuota`;

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box
        sx={{
          width:  { xs: 64, sm: 76, md: 88 },
          height: { xs: 64, sm: 76, md: 88 },
          borderRadius: '50%',
          bgcolor: circleBg,
          color: circleColor,
          border: '2.5px solid',
          borderColor,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {player ? (
          <>
            <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.55rem' }, fontWeight: 700, lineHeight: 1, color: 'inherit' }}>
              {shirtDisplay}
            </Typography>
            {roleLabel && (
              <Typography sx={{ fontSize: { xs: 9, sm: 10, md: 11 }, lineHeight: 1, mt: '2px', color: 'inherit', opacity: 0.85, fontWeight: 600 }}>
                {roleLabel}
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 10, opacity: 0.25, color: 'rgba(255,255,255,0.5)' }}>{pos}</Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// ─── LiberoIndicator ─────────────────────────────────────────────────────────

function LiberoIndicator({ player }: { player: LivePlayer }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4, alignSelf: 'center' }}>
      <Typography sx={{ fontSize: 9, color: '#f9a825', opacity: 0.75, letterSpacing: 1 }}>L</Typography>
      <Tooltip title={`Libero · #${player.shirtNumber}${player.lastName ? ` ${player.lastName}` : ''}`} arrow placement="top">
        <Box sx={{
          width: { xs: 44, sm: 52, md: 60 }, height: { xs: 44, sm: 52, md: 60 },
          borderRadius: '50%', bgcolor: '#f9a825', border: '2.5px solid #e65100',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1.1rem' }, fontWeight: 700, lineHeight: 1, color: '#000' }}>
            {player.shirtNumber || '?'}
          </Typography>
          <Typography sx={{ fontSize: { xs: 9, sm: 10, md: 11 }, lineHeight: 1, mt: '1px', fontWeight: 700, color: '#000' }}>L</Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}

// ─── TeamColumns ─────────────────────────────────────────────────────────────

interface TeamColumnsProps {
  cols: number[][];
  lineup: FieldPosition[];
  callup: LivePlayer[];
  shirtColor: string;
  setterColor: string;
}

function TeamColumns({ cols, lineup, callup, shirtColor, setterColor }: TeamColumnsProps) {
  const posMap = new Map(lineup.map((fp) => [fp.position, fp]));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, sm: 1.5, md: 2 } }}>
      {cols.map((col, ci) => (
        <Box key={ci} sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5, md: 2 } }}>
          {col.map((pos) => {
            const fp     = posMap.get(pos);
            const player = fp ? callup.find((p) => p.id === fp.playerId) : undefined;
            const key    = `${pos}-${fp?.playerId ?? 'empty'}`;

            if (pos === 1) {
              return (
                <Box key={key} sx={{ p: 0.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', border: '1px dashed rgba(255,255,255,0.4)' }}>
                  <PlayerCircle pos={pos} fieldPos={fp} player={player} shirtColor={shirtColor} setterColor={setterColor} />
                </Box>
              );
            }
            return <PlayerCircle key={key} pos={pos} fieldPos={fp} player={player} shirtColor={shirtColor} setterColor={setterColor} />;
          })}
        </Box>
      ))}
    </Box>
  );
}

// ─── VolleyballFieldView ──────────────────────────────────────────────────────

export interface VolleyballFieldViewProps {
  homeLineup: FieldPosition[];
  awayLineup: FieldPosition[];
  homeCallup: LivePlayer[];
  awayCallup: LivePlayer[];
  homeShirtColor: string;
  awayShirtColor: string;
  homeLiberoId: number | null;
  awayLiberoId: number | null;
  homeTeamName: string;
  awayTeamName: string;
  swapped?: boolean;
}

function getBenchLibero(
  liberoId: number | null,
  lineup: FieldPosition[],
  callup: LivePlayer[],
): LivePlayer | undefined {
  if (liberoId === null) return undefined;
  if (lineup.some((fp) => fp.playerId === liberoId)) return undefined;
  return callup.find((p) => p.id === liberoId);
}

export default function VolleyballFieldView({
  homeLineup, awayLineup, homeCallup, awayCallup,
  homeShirtColor, awayShirtColor, homeLiberoId, awayLiberoId,
  homeTeamName, awayTeamName, swapped = false,
}: VolleyballFieldViewProps) {
  const leftSide   = swapped ? 'AWAY' : 'HOME';
  const leftLineup  = leftSide === 'HOME' ? homeLineup  : awayLineup;
  const leftCallup  = leftSide === 'HOME' ? homeCallup  : awayCallup;
  const leftColor   = leftSide === 'HOME' ? homeShirtColor : awayShirtColor;
  const leftLibId   = leftSide === 'HOME' ? homeLiberoId   : awayLiberoId;
  const leftName    = leftSide === 'HOME' ? homeTeamName   : awayTeamName;

  const rightSide   = swapped ? 'HOME' : 'AWAY';
  const rightLineup = rightSide === 'HOME' ? homeLineup  : awayLineup;
  const rightCallup = rightSide === 'HOME' ? homeCallup  : awayCallup;
  const rightColor  = rightSide === 'HOME' ? homeShirtColor : awayShirtColor;
  const rightLibId  = rightSide === 'HOME' ? homeLiberoId   : awayLiberoId;
  const rightName   = rightSide === 'HOME' ? homeTeamName   : awayTeamName;

  const leftLibero  = getBenchLibero(leftLibId,  leftLineup,  leftCallup);
  const rightLibero = getBenchLibero(rightLibId, rightLineup, rightCallup);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', py: 1.5, px: 1 }}>

      {/* SINISTRA */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: { xs: 9, sm: 11 }, opacity: 0.35, color: 'white' }}>{leftName}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
          {leftLibero && <LiberoIndicator player={leftLibero} />}
          <TeamColumns cols={HOME_COLS} lineup={leftLineup} callup={leftCallup} shirtColor={leftColor} setterColor={rightColor} />
        </Box>
      </Box>

      {/* RETE */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx: { xs: 1.5, sm: 2.5, md: 3.5 }, alignSelf: 'stretch', pt: '1.4rem' }}>
        <Box sx={{ width: 5, flex: 1, bgcolor: 'grey.500', borderRadius: 0.5, position: 'relative' }}>
          <Chip label="RETE" size="small" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)', height: 16, fontSize: 9, bgcolor: 'grey.600', color: 'rgba(255,255,255,0.7)', letterSpacing: 1, '& .MuiChip-label': { px: 0.75 } }} />
        </Box>
      </Box>

      {/* DESTRA */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: { xs: 9, sm: 11 }, opacity: 0.35, color: 'white' }}>{rightName}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
          <TeamColumns cols={AWAY_COLS} lineup={rightLineup} callup={rightCallup} shirtColor={rightColor} setterColor={leftColor} />
          {rightLibero && <LiberoIndicator player={rightLibero} />}
        </Box>
      </Box>
    </Box>
  );
}
